import { lazy, Suspense, useMemo, useState } from 'react';
import { RouterProvider, createBrowserRouter, Outlet } from 'react-router-dom';
import MainLoader from "../components/common/MainLoader";
import AdminRoutes from "./AdminRoutes";
import CustomerRoutes from './CustomerRoutes';
import CommonRoutes from './CommonRoutes';
import PartnerRoutes from './PartnerRoutes';
import UnprotectedRoutes from './UnprotectedRoutes';
import AlwaysAccessibleRoutes from './unprotected/AlwaysAccessibleRoutes';
import { useStore } from '../components/providers/StoreContext';
import { SYSTEM_USER_TYPES } from '../constants/common.constant';
import { Navigate } from "react-router-dom";
import Loader from '../components/common/Loader';
import ErrorBoundary from '../ErrorBoundary';
import IframeRouteManager from '../components/common/IframeRouteManager';

const PageNotFound = lazy(() => import("../components/common/PageNotFound"));
const Header = lazy(() => import('../components/common/layout/Header'))
const SideNav = lazy(() => import('../components/common/layout/SideNav'));

const getRoutes = (store) => {

    const { user } = store;
    const userType = user.systemUserTypeId;

    const accessControl = JSON.parse(user.accessControlJson);

    const allowedModules = {};
    const allowedSubModules = {};

    accessControl.Access.forEach(module => {
        module.submodule.forEach(submodule => {
            allowedSubModules[submodule.smid] = true;
        })
        allowedModules[module.moduleId] = true;
    });
    let allRoutes = [];

    switch (userType) {
        case SYSTEM_USER_TYPES.ADMIN:
            allRoutes = [...AdminRoutes, ...CommonRoutes];
            break;

        case SYSTEM_USER_TYPES.CLIENT:
            allRoutes = [...CustomerRoutes, ...CommonRoutes];
            break;

        case SYSTEM_USER_TYPES.REFERRAL:
        case SYSTEM_USER_TYPES.ALTAIR_CLIENT:
        case SYSTEM_USER_TYPES.PARTNER:
            allRoutes = [...PartnerRoutes, ...CommonRoutes];
            break;
    }


    const accessedRoutes = allRoutes.filter(route => {
        return route.moduleIds?.some(moduleId => allowedModules[moduleId]) || route.submoduleIds?.some(submoduleId => allowedSubModules[submoduleId]);
    });

    if (accessedRoutes.length === 0) return [{ path: '*', id: 'PAGE_NOT_FOUND', element: <PageNotFound /> }];


    const firstRoute = { ...accessedRoutes[0] };
    accessedRoutes.push({ path: '/', id: 'FIRST_MODULE', element: <Navigate to={firstRoute.base} /> });
    accessedRoutes.push({ path: '*', id: 'PAGE_NOT_FOUND', element: <Navigate to={firstRoute.base} /> });

    return accessedRoutes;
};


const BaseLayout = () => {

    const { store } = useStore();
    const isAuthenticated = store?.isAuthenticated;
   

    return <ErrorBoundary FallbackComponent={ErrorBoundary}>
        <IframeRouteManager />
        <div className={isAuthenticated ? 'wrapper' : ''}>
            {isAuthenticated && <SideNav />}
            <div className={`main-content-width-full ${store?.isShowSideNav && isAuthenticated ? 'main-content-width' : ''}`}>
                {isAuthenticated && <Header />}
                <Suspense fallback={ <div className='fallback-loader'>
                        <Loader isLoading={true} fullPage={true} />
                        </div>}>
                    <div className={isAuthenticated ? 'main-side-section' : ''}>
                        <Outlet />
                    </div>
                </Suspense>
            </div>
        </div>
    </ErrorBoundary>
}

const Routes = () => {
    const { store } = useStore();

    const router = useMemo(() => {
        const protectedOrUnprotectedRoutes = store?.isAuthenticated
            ? getRoutes(store)
            : UnprotectedRoutes;

        return createBrowserRouter([
            // ✅ Routes WITHOUT BaseLayout
            ...AlwaysAccessibleRoutes,

            // ✅ Routes WITH BaseLayout
            {
                element: <BaseLayout />,
                children: protectedOrUnprotectedRoutes,
            },
        ]);
    }, [store?.isAuthenticated, store?.user?.systemUserTypeId, store?.user?.userId]);

    return (
        <Suspense fallback={<MainLoader />}>
            <RouterProvider router={router} />
        </Suspense>
    );
};

export default Routes;
